if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.onIntersect = this.onIntersect.bind(this);
        this.onLinkClick = this.onLinkClick.bind(this);
        this.onItemsIntersect = this.onItemsIntersect.bind(this);
        this.visibleItems = new Set();
      }

      connectedCallback() {
        this.grid = document.getElementById('product-grid');
        this.link = this.querySelector('.infinite-scroll__link');
        this.spinner = this.querySelector('.loading__spinner');
        if (!this.grid) return;

        this.currentPage = this.pageFromUrl();
        this.itemObserver = new IntersectionObserver(this.onItemsIntersect);
        this.observeItems(Array.from(this.grid.children));

        if (!this.dataset.nextUrl) return;
        this.link?.addEventListener('click', this.onLinkClick);
        this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '0px 0px 800px 0px' });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.itemObserver?.disconnect();
        this.visibleItems.clear();
        this.link?.removeEventListener('click', this.onLinkClick);
      }

      onIntersect(entries) {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      }

      onLinkClick(event) {
        event.preventDefault();
        this.load();
      }

      async load() {
        if (this.loading || !this.dataset.nextUrl) return;
        this.loading = true;
        this.classList.add('infinite-scroll--loading');
        this.classList.remove('infinite-scroll--error');
        this.spinner?.classList.remove('hidden');
        this.grid.setAttribute('aria-busy', 'true');

        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

          this.appendItems(Array.from(doc.querySelectorAll('#product-grid > .grid__item')));

          const next = doc.querySelector('infinite-scroll');
          if (next?.dataset.nextUrl) {
            this.dataset.nextUrl = next.dataset.nextUrl;
            if (this.link) this.link.href = next.dataset.nextUrl;
          } else {
            this.finish();
          }
        } catch (error) {
          console.error(error);
          this.classList.add('infinite-scroll--error');
        } finally {
          this.loading = false;
          this.classList.remove('infinite-scroll--loading');
          this.spinner?.classList.add('hidden');
          this.grid.removeAttribute('aria-busy');
        }
      }

      finish() {
        delete this.dataset.nextUrl;
        this.observer?.disconnect();
        this.link?.removeEventListener('click', this.onLinkClick);
        // Keep the element alive so the page tracker keeps updating the URL.
        this.replaceChildren();
      }

      appendItems(items) {
        if (!items.length) return;
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item));
        this.grid.appendChild(fragment);
        this.observeItems(items);

        // Hand the freshly inserted cards to the theme's reveal-on-scroll observer.
        if (typeof onIntersection === 'function') {
          const revealObserver = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
          items.forEach((item) => {
            if (item.classList.contains('scroll-trigger')) revealObserver.observe(item);
          });
        } else {
          items.forEach((item) => item.classList.add('scroll-trigger--cancel'));
        }
      }

      // --- URL page tracking -------------------------------------------------

      observeItems(items) {
        items.forEach((item) => {
          if (item.dataset.page) this.itemObserver.observe(item);
        });
      }

      onItemsIntersect(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visibleItems.add(entry.target);
          else this.visibleItems.delete(entry.target);
        });
        if (!this.visibleItems.size) return;

        // The page of the topmost visible card is the page the visitor is "on".
        let topItem = null;
        this.visibleItems.forEach((item) => {
          if (!topItem || item.getBoundingClientRect().top < topItem.getBoundingClientRect().top) topItem = item;
        });
        this.setPage(Number(topItem.dataset.page));
      }

      pageFromUrl() {
        return Number(new URLSearchParams(window.location.search).get('page')) || 1;
      }

      setPage(page) {
        if (!page || page === this.currentPage) return;
        this.currentPage = page;

        const url = new URL(window.location.href);
        if (page > 1) url.searchParams.set('page', page);
        else url.searchParams.delete('page');

        // Mirror facets.js history state so popstate handling stays consistent.
        const searchParams = url.searchParams.toString();
        history.replaceState({ ...history.state, searchParams }, '', url);
        if (typeof FacetFiltersForm !== 'undefined') {
          FacetFiltersForm.searchParamsInitial = searchParams;
          FacetFiltersForm.searchParamsPrev = searchParams;
        }
      }
    }
  );
}
